import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';

// Tables à inclure dans le backup complet (avec leur libellé sheet)
const TABLES = [
  'clients',
  'artistes',
  'artistes_domaines',
  'prestations',
  'prestations_artistes',
  'devis',
  'devis_lignes',
  'factures',
  'facture_lignes',
  'relances',
  'pieces_jointes',
  'emetteur',
  'referentiels',
  'role_permissions',
  'user_roles',
  'profiles',
  'audit_log',
] as const;

type TableName = (typeof TABLES)[number];

async function fetchAll(table: TableName): Promise<any[]> {
  // Pagination par 1000 (limite Supabase) — récupère TOUT
  const all: any[] = [];
  let from = 0;
  const PAGE = 1000;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await supabase
      .from(table as any)
      .select('*')
      .range(from, from + PAGE - 1);
    if (error) throw new Error(`${table} : ${error.message}`);
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

function aoaFromRows(rows: any[]): any[][] {
  if (rows.length === 0) return [['(aucune donnée)']];
  const cols = Array.from(
    rows.reduce<Set<string>>((s, r) => {
      Object.keys(r).forEach((k) => s.add(k));
      return s;
    }, new Set<string>()),
  );
  const header = cols;
  const body = rows.map((r) =>
    cols.map((c) => {
      const v = r[c];
      if (v === null || v === undefined) return '';
      if (typeof v === 'object') return JSON.stringify(v);
      return v;
    }),
  );
  return [header, ...body];
}

export interface BackupProgress {
  current: number;
  total: number;
  table: string;
}

export async function exportFullBackup(
  onProgress?: (p: BackupProgress) => void,
): Promise<void> {
  const zip = new JSZip();
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const folder = zip.folder(`cygnature-backup-${stamp}`)!;

  // Workbook Excel global
  const wb = XLSX.utils.book_new();
  const summary: { table: string; rows: number }[] = [];

  for (let i = 0; i < TABLES.length; i++) {
    const table = TABLES[i];
    onProgress?.({ current: i + 1, total: TABLES.length, table });

    let rows: any[] = [];
    try {
      rows = await fetchAll(table);
    } catch (e) {
      console.warn(`[backup] ${table} ignorée :`, e);
      summary.push({ table, rows: -1 });
      continue;
    }

    summary.push({ table, rows: rows.length });

    // JSON brut (intégrité totale)
    folder.file(`json/${table}.json`, JSON.stringify(rows, null, 2));

    // Feuille Excel (Excel limite les noms de feuille à 31 chars)
    const sheet = XLSX.utils.aoa_to_sheet(aoaFromRows(rows));
    XLSX.utils.book_append_sheet(wb, sheet, table.slice(0, 31));
  }

  // Excel global
  const xlsxBuf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  folder.file(`cygnature-export-${stamp}.xlsx`, xlsxBuf);

  // Manifest lisible
  const manifest = [
    `Backup Cygnature Production`,
    `Généré le : ${new Date().toLocaleString('fr-FR')}`,
    ``,
    `Contenu :`,
    `- cygnature-export-${stamp}.xlsx : toutes les tables dans un seul classeur Excel`,
    `- json/ : copie brute en JSON de chaque table (intégrité totale, restaurable)`,
    ``,
    `Détail par table :`,
    ...summary.map((s) =>
      s.rows < 0 ? `  ⚠ ${s.table} : non accessible (droits insuffisants)` : `  • ${s.table} : ${s.rows} ligne(s)`,
    ),
    ``,
    `⚠️ Ce fichier contient des données personnelles. À conserver en lieu sûr.`,
  ].join('\n');
  folder.file('README.txt', manifest);

  // Génère et télécharge
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cygnature-backup-${stamp}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
